import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useDrafts, useOnline } from "../lib/useOffline";
import { deleteDraft, syncDrafts } from "../lib/offline";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CloudOff, CheckCircle2, RotateCw, Trash2, AlertTriangle, Wifi, WifiOff, ArrowRight } from "lucide-react";

const STATUS_STYLES = {
  pending: { bg: "bg-[#FFF7E8]", border: "border-[#D08C38]", text: "text-[#9B6716]", label: "Pending sync" },
  syncing: { bg: "bg-[#F4F1EA]", border: "border-[#E6E2D8]", text: "text-[#5C5854]", label: "Syncing…" },
  synced: { bg: "bg-[#EEF2EE]", border: "border-[#B5C2B8]", text: "text-[#36453A]", label: "Synced" },
  error: { bg: "bg-[#FFF1E5]", border: "border-[#C47F4B]", text: "text-[#B45B3E]", label: "Sync error" },
};

export default function Drafts() {
  const online = useOnline();
  const { drafts, pending } = useDrafts();
  const [busy, setBusy] = useState(false);

  const handleSync = async () => {
    setBusy(true);
    try {
      const result = await syncDrafts({ force: true });
      if (result.skipped) {
        toast.error(result.reason === "offline" ? "You are offline — drafts will sync automatically when reconnected." : "Sync already in progress.");
      } else {
        toast.success(`Synced ${result.synced} of ${result.total} draft${result.total === 1 ? "" : "s"}${result.failed ? ` · ${result.failed} failed` : ""}.`);
      }
    } catch (e) {
      toast.error(e.message || "Sync failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (local_id) => {
    if (!window.confirm("Delete this offline draft? This cannot be undone.")) return;
    await deleteDraft(local_id);
    toast.success("Draft removed.");
  };

  return (
    <div className="space-y-8" data-testid="drafts-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">Field mode</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">Offline drafts &amp; sync</h1>
          <p className="text-[13.5px] text-[#5C5854] mt-2 max-w-2xl">
            Records you save while offline live here on your device. They sync to SeedKeepers automatically as soon as
            you reconnect — or you can sync them manually below.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-[12px] font-semibold ${
              online
                ? "bg-[#EEF2EE] border-[#B5C2B8] text-[#36453A]"
                : "bg-[#FFF7E8] border-[#D08C38] text-[#9B6716]"
            }`}
            data-testid="connection-status"
          >
            {online ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            {online ? "Online" : "Offline"}
          </div>
          <Button
            onClick={handleSync}
            disabled={busy || !online || pending === 0}
            className="bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7] gap-2"
            data-testid="sync-now-button"
          >
            <RotateCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Sync now {pending > 0 && `· ${pending}`}
          </Button>
        </div>
      </header>

      {drafts.length === 0 && (
        <Card className="p-10 border-dashed border-[#E6E2D8] bg-transparent text-center" data-testid="drafts-empty">
          <CloudOff className="w-8 h-8 mx-auto text-[#8B5A2B]" strokeWidth={1.4} />
          <p className="font-display text-lg font-medium mt-3 text-[#2C2A28]">No offline drafts.</p>
          <p className="text-[13px] text-[#5C5854] mt-1 max-w-md mx-auto">
            When you record a seed collection without signal, it will be stored here until you&rsquo;re back online.
          </p>
          <Link to="/collections/new" className="inline-block mt-5">
            <Button className="bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7] gap-2" data-testid="drafts-new-collection">
              Record a collection <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </Card>
      )}

      <div className="space-y-3">
        {drafts.map((d) => {
          const s = STATUS_STYLES[d.status] || STATUS_STYLES.pending;
          const p = d.payload || {};
          return (
            <Card key={d.local_id} className={`p-5 border ${s.border} ${s.bg}`} data-testid={`draft-${d.local_id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold ${s.text}`}>{s.label}</span>
                    {d.status === "error" && <AlertTriangle className="w-3.5 h-3.5 text-[#B45B3E]" />}
                    {d.status === "synced" && <CheckCircle2 className="w-3.5 h-3.5 text-[#36453A]" />}
                    <span className="text-[10px] font-mono-data text-[#8A857D] ml-2">{d.local_id}</span>
                  </div>
                  <div className="mt-2 font-serif-display italic text-[18px] text-[#2C2A28]">
                    {p.species_scientific_name || "Unidentified species"}
                  </div>
                  <div className="text-[12.5px] text-[#5C5854]">{p.species_common_name || "—"}</div>
                  <div className="grid sm:grid-cols-4 gap-3 mt-3 text-[12px] text-[#5C5854]">
                    <div><span className="text-[#8A857D] uppercase tracking-[0.16em] text-[10px]">Collected</span><br />{p.date_collected || "—"}</div>
                    <div><span className="text-[#8A857D] uppercase tracking-[0.16em] text-[10px]">Project</span><br />{p.project_name || "—"}</div>
                    <div><span className="text-[#8A857D] uppercase tracking-[0.16em] text-[10px]">TO group</span><br />{p.cultural?.traditional_owner_group || "—"}</div>
                    <div><span className="text-[#8A857D] uppercase tracking-[0.16em] text-[10px]">Saved on</span><br />{new Date(d.created_at).toLocaleString()}</div>
                  </div>
                  {d.error && (
                    <div className="mt-3 text-[12px] text-[#B45B3E]">Last error: {d.error}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.status === "synced" && d.server_id && (
                    <Link to={`/collections/${d.server_id}`} className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8B5A2B] underline-offset-4 hover:underline" data-testid={`open-synced-${d.local_id}`}>
                      Open record →
                    </Link>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => handleDelete(d.local_id)}
                    className="border-[#E6E2D8] text-[#B45B3E] hover:bg-[#FFF1E5] gap-1.5"
                    data-testid={`delete-draft-${d.local_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
